import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import styles from './Auth.module.css';

export default function Login() {
    const { perform_login, login } = useAuth();
    const navigate = useNavigate();

    const [mode, setMode] = useState('personal'); // 'personal' | 'org' | 'edu'
    const [orgName, setOrgName] = useState('');
    const [email, setEmail] = useState('');
    const [emailValid, setEmailValid] = useState(true);
    const [password, setPassword] = useState('');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const changeMode = (nextMode) => {
        setMode(nextMode);
        setError('');
        // 개인으로 바꾸면 기관명은 의미 없으니 비워주기(선택)
        if (nextMode === 'personal') setOrgName('');
    };

    const onEmailChange = (e) => {
        const v = e.target.value.trim();
        setEmail(v);
        setEmailValid(!v || emailRegex.test(v)); // 입력 중엔 비어있으면 OK, 형식만 체크
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const needsTenant = mode === 'org' || mode === 'edu';

        if (needsTenant && !orgName.trim()) {
            setError(mode === 'edu' ? '교육기관 이름을 입력하세요.' : '조직 이름을 입력하세요.');
            return;
        }

        if (!emailRegex.test(email)) {
            setEmailValid(false);
            setError('이메일 형식이 올바르지 않습니다.');
            return;
        }

        if (!email.trim() || !password) {
            setError('이메일과 비밀번호를 입력하세요.');
            return;
        }

        setLoading(true);
        try {
            const body = new URLSearchParams();

            // org/edu: email:S:orgName(tenant)
            // personal: email:S:email(tenant=개인)
            const tenant = needsTenant ? orgName.trim() : email.trim();
            body.set('username', `${email.trim()}:S:${tenant}`);
            body.set('password', password);

            const res = await perform_login(body.toString());
            if (res.ok) {
                const data = await res.json();
                if (data?.authenticated) {
                    login(data?.id, data?.tenant_id, data?.name, data?.user_key, data?.role);
                    navigate('/dashboard');
                } else {
                    setError(data?.message || '로그인에 실패했습니다.');
                }
            } else {
                setError('로그인 실패: 서버 오류가 발생했습니다.');
            }
        } catch (err) {
            setError('네트워크 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const tenantLabel = mode === 'edu' ? '교육기관 이름' : '조직 이름';
    const tenantPlaceholder = mode === 'edu' ? '예) firsim-university' : '예) realtime-tech';

    return (
        <main className={styles.container}>
            <div className={styles.card}>
                <h2 className={styles.title}>로그인</h2>

                {/* 개인 / 조직 / 교육기관 Segmented */}
                <div className={styles.segmented}>
                    <button type="button" className={`${styles.option} ${mode === 'personal' ? styles.optionActive : ''}`} onClick={() => changeMode('personal')}>
                        개인
                    </button>
                    <button type="button" className={`${styles.option} ${mode === 'org' ? styles.optionActive : ''}`} onClick={() => changeMode('org')}>
                        조직
                    </button>
                    <button type="button" className={`${styles.option} ${mode === 'edu' ? styles.optionActive : ''}`} onClick={() => changeMode('edu')}>
                        교육기관
                    </button>
                </div>

                <form className={styles.form} onSubmit={onSubmit} noValidate>
                    {/* 조직/교육기관: 기관명 입력 폼 (동일) */}
                    {(mode === 'org' || mode === 'edu') && (
                        <label className={styles.inputRow}>
                            <span className={styles.inputLabel}>{tenantLabel}</span>
                            <input type="text" placeholder={tenantPlaceholder} value={orgName} onChange={(e) => setOrgName(e.target.value)} className={styles.input} />
                        </label>
                    )}

                    <label className={styles.inputRow}>
                        <span className={styles.inputLabel}>이메일</span>
                        <input
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={onEmailChange}
                            className={`${styles.input} ${!emailValid ? styles.invalid : ''}`}
                        />
                    </label>

                    {!emailValid && <div className={styles.fieldError}>이메일 형식이 올바르지 않습니다.</div>}

                    <label className={styles.inputRow}>
                        <span className={styles.inputLabel}>비밀번호</span>
                        <input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className={styles.input} />
                    </label>

                    {error && <div className={styles.formError}>{error}</div>}

                    <button className={styles.submit} type="submit" disabled={loading}>
                        {loading ? '로그인 중...' : '로그인'}
                    </button>

                    <p className={styles.hint}>
                        {mode === 'personal' ? (
                            <>
                                개인 로그인의 경우 <strong>이메일 + 비밀번호</strong>를 입력하세요.
                            </>
                        ) : mode === 'edu' ? (
                            <>
                                교육기관 로그인의 경우 <strong>교육기관 이름 + 이메일 + 비밀번호</strong>를 입력하세요.
                            </>
                        ) : (
                            <>
                                조직 로그인의 경우 <strong>조직 이름 + 이메일 + 비밀번호</strong>를 입력하세요.
                            </>
                        )}
                    </p>
                </form>
            </div>
        </main>
    );
}
